"use client";

import { useMemo, useState } from "react";
import { Reveal } from "./Reveal";
import { SectionHeader } from "./SectionHeader";
import { Lightbox } from "./Lightbox";
import { ALBUMS, type Album } from "@/lib/diwline-data";
import { Camera } from "lucide-react";

export function GalerieDiwLine() {
  const [album, setAlbum] = useState<Album | null>(null);
  const [idx, setIdx] = useState<number | null>(null);

  const oeuvre = useMemo(() => {
    if (album && idx !== null) return album.images[idx];
    return null;
  }, [album, idx]);

  const nav = (d: number) =>
    setIdx(cur => {
      if (cur === null || !album) return null;
      return (cur + d + album.images.length) % album.images.length;
    });

  const ouvrir = (a: Album) => {
    setAlbum(a);
    setIdx(0);
  };

  return (
    <section id="galerie-diwline" className="relative bg-royal py-24 md:py-32" aria-label="Galerie DiwLine — Phase 2">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          numero="IX"
          titre="Galerie DiwLine — Phase 2"
          intro="Collection visuelle étendue — agriculture oléagineuse, récolte de palmiers, beurre & lait. Ces clichés inédits complètent les galeries curatées ci-dessus sans jamais les répéter."
        />

        <div className="grid gap-6 md:grid-cols-3">
          {ALBUMS.map((a, i) => (
            <Reveal key={a.titre} delay={i * 0.1} className="h-full">
              <button
                type="button"
                onClick={() => ouvrir(a)}
                className="group relative block h-full w-full overflow-hidden rounded-sm border border-gold/25 text-left focus-visible:outline-gold"
                aria-label={`Ouvrir l'album : ${a.titre}`}
              >
                <img
                  src={a.couverture}
                  alt={a.titre}
                  loading="lazy"
                  className="aspect-[4/5] w-full object-cover transition-transform duration-[1.2s] group-hover:scale-108"
                />
                <span className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-black/10" />
                <span className="absolute top-4 right-4 inline-flex items-center gap-1.5 rounded-full border border-gold/50 bg-black/55 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-gold-light backdrop-blur-sm">
                  <Camera className="h-3 w-3" aria-hidden />
                  {a.phase}
                </span>
                <span className="absolute bottom-0 left-0 right-0 p-6">
                  <span className="block font-display text-2xl font-semibold text-gold-gradient animate-shimmer">
                    {a.titre}
                  </span>
                  <span className="mt-2 block text-[13px] leading-relaxed text-cream/75">
                    {a.description}
                  </span>
                  <span className="mt-4 inline-block text-[11px] font-semibold uppercase tracking-[0.25em] text-gold">
                    Ouvrir l&apos;album ({a.images.length} clichés)
                  </span>
                </span>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <Lightbox oeuvre={oeuvre} onClose={() => { setAlbum(null); setIdx(null); }} onPrev={() => nav(-1)} onNext={() => nav(1)} />
    </section>
  );
}
